/**
 * Gayri Resmi Kasa — Gizli modül (Faz 34.61)
 * Root: onurselcuk@mssinsaat.com.tr
 * Guard'ı AuthContext.kasaAccess yönetir; yetkisiz görürse ana sayfaya atılır.
 * Kasalar şantiye-bazlıdır. İşlemlere opsiyonel dekont (Emergent Object Storage).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Shell from "@/components/Shell";
import { API, formatErr, useAuth } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { kasaCaseInput } from "@/lib/textFormat";
import {
  Vault, Plus, PencilSimple, TrashSimple, Lock, LockOpen, Receipt,
  TrendUp, TrendDown, Paperclip, DownloadSimple, X, UsersThree,
  Shield, EnvelopeSimple, CurrencyCircleDollar, MapPin, ArrowLeft,
  FileXls, UploadSimple, FilePdf,
} from "@phosphor-icons/react";

const fmtTL = (n) => new Intl.NumberFormat("tr-TR", {
  style: "currency", currency: "TRY", maximumFractionDigits: 2,
}).format(Number(n) || 0);

// ISO (YYYY-MM-DD) → GG.AA.YYYY
const fmtDateTR = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};

// -- Turkish money input helpers -----------------------------------
// "1250.5"  → "1.250,50"
// "1.250,5" / "1250,5" / "1,250.5" → 1250.5
const parseTR = (s) => {
  if (s === null || s === undefined) return 0;
  if (typeof s === "number") return s;
  let str = String(s).trim().replace(/\s|₺/g, "");
  if (!str) return 0;
  const hasComma = str.includes(",");
  const hasDot = str.includes(".");
  if (hasComma && hasDot) {
    // Türk formatı: nokta binlik, virgül ondalık
    str = str.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    str = str.replace(",", ".");
  }
  const n = parseFloat(str);
  return isNaN(n) ? 0 : n;
};
const formatTRFixed = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return "";
  return num.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
// Satır editöründe kullanıcı yazarken normalize et: sadece digit + 1 virgül
// + istenirse binlik nokta eklemeden kısmen formatla.
const sanitizeInput = (s) => {
  if (!s) return "";
  // İzinli: digit, nokta, virgül, eksi
  let str = String(s).replace(/[^\d.,-]/g, "");
  // Çift eksi vb. yok et
  str = str.replace(/-/g, (m, i) => (i === 0 ? "-" : ""));
  return str;
};

// Odaklanınca ham numerik; blur'da güzel biçim. Backing numeric props.value.
function MoneyInput({ value, onChange, disabled, placeholder,
                     className = "", dataTestId }) {
  const [raw, setRaw] = React.useState(
    value === "" || value === null || value === undefined
      ? ""
      : formatTRFixed(Number(value) || 0),
  );
  const [focused, setFocused] = React.useState(false);
  // Dış value prop değiştiyse (ör. form reset), input'u güncelle
  React.useEffect(() => {
    if (focused) return;
    const str = value === "" || value === null || value === undefined
      ? ""
      : formatTRFixed(Number(value) || 0);
    setRaw(str);
  }, [value, focused]);
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={raw}
      disabled={disabled}
      placeholder={placeholder || "0,00"}
      className={`font-mono text-right ${className}`}
      data-testid={dataTestId}
      onFocus={(e) => {
        setFocused(true);
        // Odaklanınca binlik noktaları kaldır — düzenlemesi kolay olsun
        const n = parseTR(raw);
        setRaw(n ? String(n).replace(".", ",") : "");
        setTimeout(() => e.target.select(), 0);
      }}
      onChange={(e) => {
        const clean = sanitizeInput(e.target.value);
        setRaw(clean);
        onChange(parseTR(clean));
      }}
      onBlur={() => {
        setFocused(false);
        const n = parseTR(raw);
        setRaw(n ? formatTRFixed(n) : "");
        onChange(n);
      }}
    />
  );
}

const EMPTY_BOX = {
  name: "", site_id: "", opening_balance: 0, opening_date: "", notes: "",
};
const EMPTY_TX = {
  type: "cikis", amount: "", date: new Date().toISOString().slice(0, 10),
  category: "", counterparty: "", description: "",
};

export default function GayriResmiKasa() {
  const { kasaAccess, user } = useAuth();
  const { sites } = useSites();
  const confirm = useConfirm();

  const [boxes, setBoxes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [txRows, setTxRows] = useState([]);
  const [txSummary, setTxSummary] = useState({});
  const [loading, setLoading] = useState(false);

  // Box dialog
  const [boxOpen, setBoxOpen] = useState(false);
  const [boxEditing, setBoxEditing] = useState(null);
  const [boxForm, setBoxForm] = useState(EMPTY_BOX);

  // Tx dialog
  const [txOpen, setTxOpen] = useState(false);
  const [txEditing, setTxEditing] = useState(null);
  const [txForm, setTxForm] = useState(EMPTY_TX);

  // Whitelist panel
  const [wlOpen, setWlOpen] = useState(false);
  const [wlRows, setWlRows] = useState([]);
  const [wlEmail, setWlEmail] = useState("");
  const [wlRootEmail, setWlRootEmail] = useState("");

  // Attachment input
  const attachInputRef = useRef(null);
  const [attachTxId, setAttachTxId] = useState(null);
  // Bulk import input
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  // Rapor dönemi
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const selected = useMemo(
    () => boxes.find((b) => b.id === selectedId) || null,
    [boxes, selectedId],
  );
  const isRoot = kasaAccess?.is_root;
  const myEmail = (user?.email || "").toLowerCase();

  const loadBoxes = async () => {
    try {
      const { data } = await axios.get(`${API}/kasa/boxes`);
      setBoxes(data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadTxs = async (kid) => {
    if (!kid) { setTxRows([]); setTxSummary({}); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/kasa/transactions`, {
        params: { kasa_id: kid },
      });
      setTxRows(data.items || []);
      setTxSummary(data.summary || {});
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  const loadWhitelist = async () => {
    try {
      const { data } = await axios.get(`${API}/kasa/whitelist`);
      setWlRows(data.entries || []);
      setWlRootEmail(data.root_email || "");
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { loadBoxes(); }, []);
  useEffect(() => { loadTxs(selectedId); }, [selectedId]);
  useEffect(() => { if (wlOpen) loadWhitelist(); }, [wlOpen]);

  // ---------------- Box CRUD ----------------
  const openCreateBox = () => {
    setBoxEditing(null);
    setBoxForm({
      ...EMPTY_BOX,
      opening_date: new Date().toISOString().slice(0, 10),
    });
    setBoxOpen(true);
  };
  const openEditBox = (b) => {
    setBoxEditing(b);
    setBoxForm({
      name: b.name,
      site_id: b.site_id || "",
      opening_balance: b.opening_balance || 0,
      opening_date: b.opening_date || "",
      notes: b.notes || "",
    });
    setBoxOpen(true);
  };
  const saveBox = async () => {
    if (!boxForm.name.trim()) { toast.error("Kasa adı zorunludur"); return; }
    try {
      const payload = {
        name: boxForm.name.trim(),
        site_id: boxForm.site_id || null,
        opening_balance: Number(boxForm.opening_balance) || 0,
        opening_date: boxForm.opening_date,
        notes: boxForm.notes,
      };
      if (boxEditing) {
        await axios.patch(`${API}/kasa/boxes/${boxEditing.id}`, payload);
        toast.success("Kasa güncellendi");
      } else {
        await axios.post(`${API}/kasa/boxes`, payload);
        toast.success("Kasa oluşturuldu");
      }
      setBoxOpen(false);
      await loadBoxes();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const closeBox = async (b) => {
    const ok = await confirm({
      title: "Kasayı kapat",
      description: `"${b.name}" kasasını kapatmak istediğinize emin misiniz? Kapatılan kasaya yeni işlem eklenemez.`,
      confirmText: "Kapat", destructive: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/kasa/boxes/${b.id}/close`);
      toast.success("Kasa kapatıldı");
      await loadBoxes();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const reopenBox = async (b) => {
    try {
      await axios.post(`${API}/kasa/boxes/${b.id}/reopen`);
      toast.success("Kasa yeniden açıldı");
      await loadBoxes();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteBox = async (b) => {
    const ok = await confirm({
      title: "Kasayı sil",
      description: `"${b.name}" kasası silinecek. İşlem varsa önce silmelisiniz.`,
      confirmText: "Sil", destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/kasa/boxes/${b.id}`);
      toast.success("Kasa silindi");
      if (selectedId === b.id) setSelectedId(null);
      await loadBoxes();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---------------- Tx CRUD ----------------
  const openCreateTx = () => {
    if (!selected) return;
    setTxEditing(null);
    setTxForm(EMPTY_TX);
    setTxOpen(true);
  };
  const openEditTx = (t) => {
    setTxEditing(t);
    setTxForm({
      type: t.type, amount: t.amount, date: t.date,
      category: t.category || "", counterparty: t.counterparty || "",
      description: t.description || "",
    });
    setTxOpen(true);
  };
  const saveTx = async () => {
    if (!selected) return;
    if (!txForm.date) { toast.error("Tarih zorunludur"); return; }
    if (!Number(txForm.amount) || Number(txForm.amount) <= 0) {
      toast.error("Tutar sıfırdan büyük olmalı"); return;
    }
    try {
      if (txEditing) {
        await axios.patch(`${API}/kasa/transactions/${txEditing.id}`, {
          type: txForm.type, amount: Number(txForm.amount), date: txForm.date,
          category: txForm.category, counterparty: txForm.counterparty,
          description: txForm.description,
        });
        toast.success("İşlem güncellendi");
      } else {
        await axios.post(`${API}/kasa/transactions`, {
          kasa_id: selected.id, type: txForm.type, amount: Number(txForm.amount),
          date: txForm.date, category: txForm.category,
          counterparty: txForm.counterparty, description: txForm.description,
        });
        toast.success("İşlem eklendi");
      }
      setTxOpen(false);
      await Promise.all([loadBoxes(), loadTxs(selected.id)]);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteTx = async (t) => {
    const ok = await confirm({
      title: "İşlemi sil", description: "Bu işlemi silmek istediğinize emin misiniz?",
      confirmText: "Sil", destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/kasa/transactions/${t.id}`);
      toast.success("İşlem silindi");
      await Promise.all([loadBoxes(), loadTxs(selected.id)]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---------------- Attachment ----------------
  const triggerAttach = (tid) => {
    setAttachTxId(tid);
    attachInputRef.current?.click();
  };
  const onAttachFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file || !attachTxId) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/kasa/transactions/${attachTxId}/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Dekont yüklendi");
      await loadTxs(selected.id);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAttachTxId(null); }
  };
  const downloadAttach = async (t) => {
    try {
      const r = await axios.get(`${API}/kasa/transactions/${t.id}/attachment`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = t.attachment_name || "dekont";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removeAttach = async (t) => {
    const ok = await confirm({
      title: "Dekontu kaldır", description: "Eklenen dekont silinsin mi?",
      confirmText: "Kaldır", destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/kasa/transactions/${t.id}/attachment`);
      toast.success("Dekont kaldırıldı");
      await loadTxs(selected.id);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---------------- Bulk Excel Import ----------------
  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/kasa/transactions/import-template.xlsx`, {
        responseType: "blob",
        params: selected?.id ? { kasa_id: selected.id } : {},
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gayri_resmi_kasa_toplu_islem.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const triggerImport = () => importInputRef.current?.click();
  const downloadReport = async () => {
    if (!selected) return;
    try {
      const params = {};
      if (reportFrom) params.date_from = reportFrom;
      if (reportTo) params.date_to = reportTo;
      // Blob + fresh fetch — tarayıcı URL-cache'ini bypass eder; her tıklamada
      // sunucudan güncel PDF indirilir ve `window.open(url, "_blank")`'ın
      // stale önizleme sorununu önler.
      const r = await axios.get(
        `${API}/kasa/boxes/${selected.id}/report.pdf`,
        { params, responseType: "blob" },
      );
      const blobUrl = URL.createObjectURL(
        new Blob([r.data], { type: "application/pdf" }),
      );
      const safeName = (selected.name || "kasa").trim().replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, "_");
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `kasa_raporu_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Bellek sızıntısını önle — tab açıldıktan sonra blob URL'i serbest bırak.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      setReportOpen(false);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const quickRange = (type) => {
    const now = new Date();
    const toIso = (d) => d.toISOString().slice(0, 10);
    if (type === "all") { setReportFrom(""); setReportTo(""); return; }
    if (type === "this_month") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setReportFrom(toIso(first)); setReportTo(toIso(last));
    } else if (type === "last_month") {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      setReportFrom(toIso(first)); setReportTo(toIso(last));
    } else if (type === "ytd") {
      setReportFrom(toIso(new Date(now.getFullYear(), 0, 1)));
      setReportTo(toIso(now));
    } else if (type === "last_30") {
      const past = new Date(now); past.setDate(now.getDate() - 30);
      setReportFrom(toIso(past)); setReportTo(toIso(now));
    }
  };
  const onImportFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file || !selected) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/kasa/transactions/import?kasa_id=${selected.id}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const inserted = r.data.inserted || 0;
      const errCount = (r.data.errors || []).length;
      if (inserted > 0 && errCount === 0) {
        toast.success(`${inserted} işlem başarıyla eklendi`);
      } else if (inserted > 0 && errCount > 0) {
        toast.warning(`${inserted} işlem eklendi, ${errCount} satır hatalı`);
        console.warn("Kasa import errors:", r.data.errors);
      } else if (errCount > 0) {
        toast.error(`Hiçbir satır eklenemedi (${errCount} hata)`);
        console.warn("Kasa import errors:", r.data.errors);
      } else {
        toast.info("İşlenecek satır bulunamadı");
      }
      await Promise.all([loadBoxes(), loadTxs(selected.id)]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setImporting(false); }
  };

  // ---------------- Whitelist ----------------
  const addWl = async () => {
    const em = (wlEmail || "").trim().toLowerCase();
    if (!em) return;
    try {
      await axios.post(`${API}/kasa/whitelist`, { email: em });
      toast.success("Yetki verildi");
      setWlEmail("");
      await loadWhitelist();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delWl = async (w) => {
    const ok = await confirm({
      title: "Yetkiyi kaldır",
      description: `${w.email} yetkisi kaldırılacak.`, confirmText: "Kaldır", destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/kasa/whitelist/${w.id}`);
      toast.success("Yetki kaldırıldı");
      await loadWhitelist();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const canEditBox = (b) => b.status === "acik" && (isRoot || b.created_by_email === myEmail);
  const canCloseBox = (b) => b.status === "acik" && (isRoot || b.created_by_email === myEmail);

  // ---------------- RENDER ----------------
  if (!selected) {
    return (
      <Shell>
        <input ref={attachInputRef} type="file" className="hidden"
               accept="image/*,application/pdf" onChange={onAttachFile} />
        <div className="space-y-6" data-testid="kasa-page">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-amber-500/15 flex items-center justify-center">
                <Vault size={22} weight="duotone" className="text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1B4776]">Gayri Resmi Kasa</h1>
                <p className="text-sm text-slate-500">
                  İş bazlı kasalar — gizli modül · {kasaAccess?.is_root ? "Root yetkisi" : "Yetkili"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isRoot && (
                <Button variant="outline" onClick={() => setWlOpen(true)}
                        className="rounded-none sm:rounded-sm"
                        data-testid="kasa-whitelist-btn">
                  <Shield size={14} weight="bold" className="mr-1.5" />
                  Yetkililer
                </Button>
              )}
              <Button onClick={openCreateBox}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-none sm:rounded-sm"
                      data-testid="kasa-add-btn">
                <Plus size={14} weight="bold" className="mr-1.5" />
                Yeni Kasa Aç
              </Button>
            </div>
          </div>

          {/* Boxes Grid */}
          {boxes.length === 0 ? (
            <Card className="p-12 text-center text-slate-500 rounded-none sm:rounded-sm">
              <Vault size={48} weight="duotone" className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Henüz kasa açılmadı. "Yeni Kasa Aç" ile başlayın.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {boxes.map((b) => {
                const kapali = b.status === "kapali";
                return (
                  <Card key={b.id}
                        className={`p-5 cursor-pointer border-l-4 transition-all hover:shadow-lg ${
                          kapali
                            ? "border-l-slate-300 opacity-80 hover:opacity-100"
                            : "border-l-amber-500 hover:border-l-amber-600"
                        }`}
                        onClick={() => setSelectedId(b.id)}
                        data-testid={`kasa-card-${b.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-bold text-[#0F172A] line-clamp-1">
                            {b.name}
                          </span>
                          <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                            kapali
                              ? "bg-slate-100 text-slate-600 border-slate-300"
                              : "bg-emerald-50 text-emerald-700 border-emerald-300"
                          }`}>
                            {kapali ? "KAPALI" : "AÇIK"}
                          </span>
                        </div>
                        {b.site_code && (
                          <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                            <MapPin size={12} weight="fill" className="text-[#1B4776]/60" />
                            <span className="font-mono font-bold">{b.site_code}</span>
                            <span>— {b.site_name}</span>
                          </div>
                        )}
                        <div className="mt-1 text-[10px] text-slate-400 tracking-wide">
                          Açılış {fmtDateTR(b.opening_date)} · {b.created_by_email}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                          Cari Bakiye
                        </span>
                        <span className={`font-mono text-lg font-bold ${
                          (b.balance || 0) >= 0 ? "text-emerald-700" : "text-rose-700"
                        }`}>
                          {fmtTL(b.balance)}
                        </span>
                      </div>
                      <div className="mt-1.5 grid grid-cols-3 gap-2 text-[10px]">
                        <div className="text-slate-500">
                          <div className="text-emerald-700 font-bold">{fmtTL(b.giris_total)}</div>
                          Giriş
                        </div>
                        <div className="text-slate-500">
                          <div className="text-rose-700 font-bold">{fmtTL(b.cikis_total)}</div>
                          Çıkış
                        </div>
                        <div className="text-slate-500">
                          <div className="text-[#1B4776] font-bold">{b.tx_count || 0}</div>
                          İşlem
                        </div>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-end gap-1">
                      {canEditBox(b) && (
                        <button className="p-1.5 text-slate-400 hover:text-[#1B4776]"
                                onClick={(e) => { e.stopPropagation(); openEditBox(b); }}
                                title="Düzenle"
                                data-testid={`kasa-edit-${b.id}`}>
                          <PencilSimple size={14} weight="bold" />
                        </button>
                      )}
                      {canCloseBox(b) && (
                        <button className="p-1.5 text-slate-400 hover:text-amber-700"
                                onClick={(e) => { e.stopPropagation(); closeBox(b); }}
                                title="Kasayı kapat"
                                data-testid={`kasa-close-${b.id}`}>
                          <Lock size={14} weight="bold" />
                        </button>
                      )}
                      {kapali && (isRoot || b.created_by_email === myEmail) && (
                        <button className="p-1.5 text-slate-400 hover:text-emerald-700"
                                onClick={(e) => { e.stopPropagation(); reopenBox(b); }}
                                title="Yeniden aç"
                                data-testid={`kasa-reopen-${b.id}`}>
                          <LockOpen size={14} weight="bold" />
                        </button>
                      )}
                      {(isRoot || b.created_by_email === myEmail) && (
                        <button className="p-1.5 text-slate-400 hover:text-rose-700"
                                onClick={(e) => { e.stopPropagation(); deleteBox(b); }}
                                title="Sil"
                                data-testid={`kasa-delete-${b.id}`}>
                          <TrashSimple size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Box Dialog */}
        <BoxDialog open={boxOpen} setOpen={setBoxOpen} editing={boxEditing}
                   form={boxForm} setForm={setBoxForm} onSave={saveBox}
                   sites={sites || []} />

        {/* Whitelist Dialog */}
        {isRoot && (
          <WhitelistDialog open={wlOpen} setOpen={setWlOpen}
                            rows={wlRows} rootEmail={wlRootEmail}
                            email={wlEmail} setEmail={setWlEmail}
                            onAdd={addWl} onDelete={delWl} />
        )}
      </Shell>
    );
  }

  // ---------------- Scoped (box detail) ----------------
  const bal = txSummary.balance ?? selected.balance;
  return (
    <Shell>
      <input ref={attachInputRef} type="file" className="hidden"
             accept="image/*,application/pdf" onChange={onAttachFile} />
      <div className="space-y-4" data-testid="kasa-detail-page">
        {/* Scope Header */}
        <div className="flex items-center justify-between gap-4 p-3 bg-amber-50/70 border border-amber-300 rounded-none sm:rounded-sm">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setSelectedId(null)}
                    className="p-1.5 rounded-full bg-white border border-amber-300 text-amber-700 hover:bg-amber-100 transition"
                    data-testid="kasa-back-btn">
              <ArrowLeft size={14} weight="bold" />
            </button>
            <Vault size={18} weight="duotone" className="text-amber-700 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
                Aktif Kasa {selected.status === "kapali" && <span className="ml-1 text-rose-700">· KAPALI</span>}
              </div>
              <div className="text-sm font-bold text-[#0F172A] truncate">
                {selected.name}
                {selected.site_code && (
                  <span className="ml-2 font-mono text-xs text-slate-500">
                    [{selected.site_code}]
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Cari Bakiye</div>
            <div className={`font-mono text-lg font-bold ${
              bal >= 0 ? "text-emerald-700" : "text-rose-700"
            }`} data-testid="kasa-current-balance">
              {fmtTL(bal)}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-4 gap-3">
          <StatCard icon={CurrencyCircleDollar} label="Açılış Bakiye"
                    value={fmtTL(selected.opening_balance)} color="slate" />
          <StatCard icon={TrendUp} label="Toplam Giriş"
                    value={fmtTL(txSummary.giris_total || 0)} color="emerald" />
          <StatCard icon={TrendDown} label="Toplam Çıkış"
                    value={fmtTL(txSummary.cikis_total || 0)} color="rose" />
          <StatCard icon={Receipt} label="İşlem Sayısı"
                    value={txSummary.tx_count || 0} color="indigo" />
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-bold text-[#1B4776] uppercase tracking-widest">İşlemler</h2>
          {selected.status === "acik" && (
            <div className="flex items-center gap-2 flex-wrap">
              <ReportPopover
                open={reportOpen} setOpen={setReportOpen}
                from={reportFrom} to={reportTo}
                setFrom={setReportFrom} setTo={setReportTo}
                onQuick={quickRange} onDownload={downloadReport} />
              <Button variant="outline" onClick={downloadTemplate}
                      className="rounded-none sm:rounded-sm h-9"
                      data-testid="kasa-tx-template-btn">
                <FileXls size={14} weight="bold" className="mr-1.5 text-emerald-700" />
                Şablon İndir
              </Button>
              <Button variant="outline" onClick={triggerImport}
                      disabled={importing}
                      className="rounded-none sm:rounded-sm h-9 border-amber-400 text-amber-800 hover:bg-amber-50"
                      data-testid="kasa-tx-import-btn">
                <UploadSimple size={14} weight="bold" className="mr-1.5" />
                {importing ? "Yükleniyor..." : "Toplu İçe Aktar"}
              </Button>
              <Button onClick={openCreateTx}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-none sm:rounded-sm h-9"
                      data-testid="kasa-tx-add-btn">
                <Plus size={14} weight="bold" className="mr-1.5" /> Yeni İşlem
              </Button>
            </div>
          )}
          {selected.status === "kapali" && (
            <ReportPopover
              open={reportOpen} setOpen={setReportOpen}
              from={reportFrom} to={reportTo}
              setFrom={setReportFrom} setTo={setReportTo}
              onQuick={quickRange} onDownload={downloadReport} />
          )}
        </div>
        {/* Hidden import input */}
        <input ref={importInputRef} type="file" className="hidden"
               accept=".xlsx,.xls" onChange={onImportFile} />

        {/* Transactions */}
        {loading ? (
          <Card className="p-8 text-center text-slate-400">Yükleniyor...</Card>
        ) : txRows.length === 0 ? (
          <Card className="p-8 text-center text-slate-500">
            <Receipt size={36} weight="duotone" className="mx-auto mb-2 text-slate-300" />
            Bu kasada henüz işlem yok.
          </Card>
        ) : (
          <Card className="overflow-hidden rounded-none sm:rounded-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    <th className="px-3 py-2 text-left">Tarih</th>
                    <th className="px-3 py-2 text-left">Tip</th>
                    <th className="px-3 py-2 text-left">Kategori</th>
                    <th className="px-3 py-2 text-left">Taraf</th>
                    <th className="px-3 py-2 text-left">Açıklama</th>
                    <th className="px-3 py-2 text-right">Tutar</th>
                    <th className="px-3 py-2 text-center">Dekont</th>
                    <th className="px-3 py-2 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {txRows.map((t) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/60"
                        data-testid={`kasa-tx-row-${t.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{fmtDateTR(t.date)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                          t.type === "giris"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                            : "bg-rose-50 text-rose-700 border-rose-300"
                        }`}>
                          {t.type === "giris" ? "Giriş" : "Çıkış"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{t.category || "—"}</td>
                      <td className="px-3 py-2 text-xs">{t.counterparty || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 line-clamp-1">
                        {t.description || "—"}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${
                        t.type === "giris" ? "text-emerald-700" : "text-rose-700"
                      }`}>
                        {t.type === "giris" ? "+" : "−"}{fmtTL(t.amount)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {t.attachment_path ? (
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => downloadAttach(t)}
                                    className="p-1 text-[#1B4776] hover:text-amber-700"
                                    title={t.attachment_name}
                                    data-testid={`kasa-tx-dl-${t.id}`}>
                              <DownloadSimple size={14} weight="bold" />
                            </button>
                            {selected.status === "acik" && (
                              <button onClick={() => removeAttach(t)}
                                      className="p-1 text-slate-400 hover:text-rose-700"
                                      title="Dekontu kaldır">
                                <X size={12} weight="bold" />
                              </button>
                            )}
                          </div>
                        ) : selected.status === "acik" ? (
                          <button onClick={() => triggerAttach(t.id)}
                                  className="p-1 text-slate-400 hover:text-[#1B4776]"
                                  title="Dekont ekle"
                                  data-testid={`kasa-tx-attach-${t.id}`}>
                            <Paperclip size={14} weight="bold" />
                          </button>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {selected.status === "acik" && (
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => openEditTx(t)}
                                    className="p-1 text-slate-400 hover:text-[#1B4776]"
                                    title="Düzenle"
                                    data-testid={`kasa-tx-edit-${t.id}`}>
                              <PencilSimple size={13} weight="bold" />
                            </button>
                            <button onClick={() => deleteTx(t)}
                                    className="p-1 text-slate-400 hover:text-rose-700"
                                    title="Sil"
                                    data-testid={`kasa-tx-delete-${t.id}`}>
                              <TrashSimple size={13} weight="bold" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Tx Dialog */}
      <TxDialog open={txOpen} setOpen={setTxOpen} editing={txEditing}
                form={txForm} setForm={setTxForm} onSave={saveTx} />
    </Shell>
  );
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================
function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    emerald: "bg-emerald-500/15 text-emerald-700",
    rose: "bg-rose-500/15 text-rose-700",
    indigo: "bg-[#1B4776]/10 text-[#1B4776]",
    slate: "bg-slate-200 text-slate-700",
  };
  return (
    <Card className="p-3 rounded-none sm:rounded-sm">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${colorMap[color] || colorMap.slate}`}>
          <Icon size={16} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
          <div className="text-base font-bold font-mono text-[#0F172A] truncate">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function BoxDialog({ open, setOpen, editing, form, setForm, onSave, sites }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md rounded-none sm:rounded-sm"
                     data-testid="kasa-box-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Kasayı Düzenle" : "Yeni Kasa Aç"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Kasa Adı *</Label>
            <Input value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Ör. Ahmet Usta Boya İşi"
                   className="rounded-none sm:rounded-sm"
                   data-testid="kasa-box-name" />
          </div>
          <div>
            <Label>Şantiye</Label>
            <Select value={form.site_id || "_none"}
                    onValueChange={(v) => setForm({ ...form, site_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="rounded-none sm:rounded-sm"
                              data-testid="kasa-box-site">
                <SelectValue placeholder="Opsiyonel — şantiye bağla" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Şantiye bağlanmayacak —</SelectItem>
                {sites.filter((s) => s.status !== "arsiv").map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    <span className="font-mono text-xs mr-2">{s.code}</span>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Açılış Bakiye (₺)</Label>
              <MoneyInput value={form.opening_balance}
                           onChange={(n) => setForm({ ...form, opening_balance: n })}
                           dataTestId="kasa-box-opening"
                           className="rounded-none sm:rounded-sm" />
            </div>
            <div>
              <Label>Açılış Tarihi</Label>
              <Input type="date" value={form.opening_date}
                     onChange={(e) => setForm({ ...form, opening_date: e.target.value })}
                     className="rounded-none sm:rounded-sm" />
            </div>
          </div>
          <div>
            <Label>Notlar</Label>
            <Textarea rows={2} value={form.notes}
                       onChange={(e) => setForm({ ...form, notes: e.target.value })}
                       className="rounded-none sm:rounded-sm"
                       placeholder="Opsiyonel açıklama" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}
                  className="rounded-none sm:rounded-sm">İptal</Button>
          <Button onClick={onSave}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-none sm:rounded-sm"
                  data-testid="kasa-box-save">
            {editing ? "Güncelle" : "Kasayı Aç"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TxDialog({ open, setOpen, editing, form, setForm, onSave }) {
  const normalizeField = async (field, value) => {
    if (!value || !value.trim()) return;
    try {
      const { data } = await axios.post(`${API}/kasa/normalize-text`, { text: value });
      setForm((f) => (f[field] === value ? { ...f, [field]: data.text } : f));
    } catch (_) { /* kayıt sırasında sunucu yine düzeltir */ }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md rounded-none sm:rounded-sm"
                     data-testid="kasa-tx-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "İşlemi Düzenle" : "Yeni İşlem"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tip *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-none sm:rounded-sm"
                                data-testid="kasa-tx-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="giris">Giriş</SelectItem>
                  <SelectItem value="cikis">Çıkış</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tarih *</Label>
              <Input type="date" value={form.date}
                     onChange={(e) => setForm({ ...form, date: e.target.value })}
                     className="rounded-none sm:rounded-sm" />
            </div>
          </div>
          <div>
            <Label>Tutar (₺) *</Label>
            <MoneyInput value={form.amount}
                         onChange={(n) => setForm({ ...form, amount: n })}
                         dataTestId="kasa-tx-amount"
                         className="rounded-none sm:rounded-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Kategori</Label>
              <Input value={form.category}
                     onChange={(e) => kasaCaseInput(e, (v) => setForm((f) => ({ ...f, category: v })))}
                     onBlur={(e) => normalizeField("category", e.target.value)}
                     data-testid="kasa-tx-category"
                     placeholder="Nakliye, yemek vb."
                     className="rounded-none sm:rounded-sm" />
            </div>
            <div>
              <Label>Kime / Kimden</Label>
              <Input value={form.counterparty}
                     onChange={(e) => kasaCaseInput(e, (v) => setForm((f) => ({ ...f, counterparty: v })))}
                     onBlur={(e) => normalizeField("counterparty", e.target.value)}
                     data-testid="kasa-tx-counterparty"
                     placeholder="Ahmet Usta"
                     className="rounded-none sm:rounded-sm" />
            </div>
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea rows={2} value={form.description}
                       onChange={(e) => kasaCaseInput(e, (v) => setForm((f) => ({ ...f, description: v })))}
                     onBlur={(e) => normalizeField("description", e.target.value)}
                       data-testid="kasa-tx-description"
                       className="rounded-none sm:rounded-sm"
                       placeholder="Opsiyonel detay" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}
                  className="rounded-none sm:rounded-sm">İptal</Button>
          <Button onClick={onSave}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-none sm:rounded-sm"
                  data-testid="kasa-tx-save">
            {editing ? "Güncelle" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportPopover({ open, setOpen, from, to, setFrom, setTo, onQuick, onDownload }) {
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline"
                className="rounded-none sm:rounded-sm h-9"
                data-testid="kasa-tx-report-btn">
          <FilePdf size={14} weight="bold" className="mr-1.5 text-rose-600" />
          PDF Rapor
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 rounded-none sm:rounded-sm p-4"
                       data-testid="kasa-report-popover" align="end">
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
              Rapor Dönemi
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Boş bırakılırsa tüm işlemler listelenir.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Başlangıç</Label>
              <Input type="date" value={from}
                     onChange={(e) => setFrom(e.target.value)}
                     className="rounded-none sm:rounded-sm h-8 text-xs"
                     data-testid="kasa-report-from" />
            </div>
            <div>
              <Label className="text-[10px]">Bitiş</Label>
              <Input type="date" value={to}
                     onChange={(e) => setTo(e.target.value)}
                     className="rounded-none sm:rounded-sm h-8 text-xs"
                     data-testid="kasa-report-to" />
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {[
              ["Tümü", "all"],
              ["Bu Ay", "this_month"],
              ["Geçen Ay", "last_month"],
              ["Son 30 Gün", "last_30"],
              ["Yıl Başı", "ytd"],
            ].map(([lbl, key]) => (
              <button key={key} type="button"
                      onClick={() => onQuick(key)}
                      className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-slate-300 hover:border-rose-500 hover:text-rose-700 transition-colors"
                      data-testid={`kasa-report-quick-${key}`}>
                {lbl}
              </button>
            ))}
          </div>
          <Button onClick={onDownload}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-none sm:rounded-sm h-9"
                  data-testid="kasa-report-download-btn">
            <FilePdf size={14} weight="bold" className="mr-1.5" />
            PDF İndir
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function WhitelistDialog({ open, setOpen, rows, rootEmail, email, setEmail, onAdd, onDelete }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>      <DialogContent className="max-w-lg rounded-none sm:rounded-sm"
                     data-testid="kasa-whitelist-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield size={18} weight="duotone" className="text-amber-600" />
            Gayri Resmi Kasa — Yetkililer
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-2.5 bg-slate-50 border border-slate-200 text-xs">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={14} weight="bold" className="text-amber-700" />
              <span className="font-bold text-[#1B4776]">Root:</span>
              <span className="font-mono">{rootEmail}</span>
              <span className="ml-auto text-[9px] uppercase tracking-widest font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 border border-amber-300">
                Kalıcı
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Input value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   placeholder="E-posta ile yetki ver"
                   className="rounded-none sm:rounded-sm"
                   data-testid="kasa-wl-email-input" />
            <Button onClick={onAdd}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-none sm:rounded-sm"
                    data-testid="kasa-wl-add-btn">
              <Plus size={14} weight="bold" className="mr-1" /> Ekle
            </Button>
          </div>
          {rows.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-4">
              Henüz başka yetkili yok.
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-auto">
              {rows.map((w) => (
                <div key={w.id}
                     className="flex items-center justify-between gap-2 p-2 border border-slate-200 bg-white"
                     data-testid={`kasa-wl-row-${w.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-mono truncate">{w.email}</div>
                    <div className="text-[10px] text-slate-400">
                      {w.added_by} · {(w.added_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <button onClick={() => onDelete(w)}
                          className="p-1.5 text-slate-400 hover:text-rose-700"
                          data-testid={`kasa-wl-delete-${w.id}`}>
                    <TrashSimple size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}
                  className="rounded-none sm:rounded-sm">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
